import { bbox, featureCollection, point } from '@turf/turf';
import { Feature } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import type { Geometry, Polygon } from 'ol/geom';
import { Point } from 'ol/geom';
import { fromExtent } from 'ol/geom/Polygon';
import type { DrawEvent } from 'ol/interaction/Draw';
import Draw from 'ol/interaction/Draw';

import { DrawToolType } from '@nora-monorepo/nora-api-client';
import type { NamedLatLonCoordinate } from '@nora-monorepo/shared';
import { isNonEmptyString } from '@nora-monorepo/shared';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import {
  namedLatLonCoordinateToOlCoordinate,
  olToNamedLatLonCoordinate,
} from '#nora-ui/map/drawtools/draw-tool-utils';
import { spotStyleFunction } from '#nora-ui/map/drawtools/styles/spot-map-style';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

const directions = ['W', 'SW', 'S', 'SE', 'E', 'NE', 'N', 'NW'];

export class SpotMapTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.spot;
  options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  private readonly drawInteraction: Draw;
  private originPoint: Coordinate | null = null;
  private originFeature: Feature<Geometry> | null = null;

  private spotCoordinates: NamedLatLonCoordinate[] = [];
  private spotFeatures: Feature<Geometry>[] = [];

  // Stores how many direction labels we have created, so we can label things NW1, NW2, and so on.
  private directionCounter: Map<number, number> = new Map();

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Spot';
    this.drawInteraction = new Draw({
      source: this.editingVectorSource,
      type: 'Point',
      style: spotStyleFunction,
    });

    this.drawInteraction.on('drawend', this.onDrawEnd.bind(this));
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.addInteraction(this.drawInteraction);
  }

  override deactivate(): void {
    super.deactivate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.removeInteraction(this.drawInteraction);
  }

  onDrawEnd(event: DrawEvent): void {
    const coordinate = (event.feature.getGeometry() as Point).getCoordinates();
    // Remove the event feature to prevent duplicate display
    this.editingVectorSource.removeFeature(event.feature);

    if (!this.originPoint) {
      this.originPoint = coordinate;
      this.originFeature = new Feature({
        geometry: new Point(coordinate),
      });
      this.originFeature.setProperties({
        ...this.getDefaultProperties(),
        labelText: 'Origin',
        isOrigin: true,
      });
      this.originFeature.setStyle(spotStyleFunction);
      this.editingVectorSource.addFeature(this.originFeature);

      const namedCoordinate = olToNamedLatLonCoordinate(coordinate, 'Origin');
      this.spotCoordinates.push(namedCoordinate);
    }
    else {
      const label = this.getLabel(coordinate);

      const pointFeature = new Feature({
        geometry: new Point(coordinate),
      });
      pointFeature.setProperties({
        ...this.getDefaultProperties(),
        labelText: label,
        isOrigin: false,
      });
      pointFeature.setStyle(spotStyleFunction);
      this.spotFeatures.push(pointFeature);
      this.editingVectorSource.addFeature(pointFeature);

      // Store the point coordinate using NamedLatLonCoordinate format
      const namedCoordinate = olToNamedLatLonCoordinate(coordinate, label);
      this.spotCoordinates.push(namedCoordinate);
    }

    this.updateOriginFeature();
    this.markDirty();
  }

  private updateOriginFeature(): void {
    if (this.originFeature && this.spotCoordinates.length > 0) {
      // Create a bounding rectangle around all coordinates
      const boundingGeometry = this.createBoundingRectangle();

      // Update the origin feature geometry to the bounding rectangle
      this.originFeature.setGeometry(boundingGeometry);

      // Update the origin feature with all spot coordinates
      this.originFeature.setProperties({
        ...this.getDefaultProperties(),
        coordinates: [...this.spotCoordinates],
      });
      this.feature = this.originFeature;
    }
  }

  private createBoundingRectangle(): Polygon {
    const turfPoints = this.spotCoordinates.map(coordinate =>
      point(namedLatLonCoordinateToOlCoordinate(coordinate)),
    );
    const collection = featureCollection(turfPoints);
    const bounds = bbox(collection);
    this.markDirty();
    return fromExtent(bounds);
  }

  private getLabel(coordinate: Coordinate): string {
    if (!this.originPoint) {
      return '';
    }
    const [originX, originY] = this.originPoint;
    const [x, y] = coordinate;
    if (
      originX === undefined
      || originY === undefined
      || x === undefined
      || y === undefined
    ) {
      return '';
    }
    const deltaX = x - originX;
    const deltaY = y - originY;
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

    // We pick the direction based on the angle from the origin
    const directionIndex = Math.round(((angle + 180) % 360) / 45) % 8;
    const directionCount = (this.directionCounter.get(directionIndex) ?? 0) + 1;
    this.directionCounter.set(directionIndex, directionCount);
    return `${directions[directionIndex]}${directionCount}`;
  }

  override loadFeature(feature: Feature<Geometry>): boolean {
    // Extract draw tool options and update styling
    const featureProperties = extractDrawToolOptions(feature);
    this.drawToolManager.updateDrawStyleOptions(featureProperties);

    // Get the stored spot coordinates
    const storedSpotCoordinates = feature.get('coordinates') as
      | NamedLatLonCoordinate[]
      | undefined;

    if (!Array.isArray(storedSpotCoordinates)) {
      return false;
    }

    // Reset state
    this.reset();
    this.spotCoordinates = [...storedSpotCoordinates];

    // Find the origin coordinate (should be the first one with name 'Origin')
    const originData = storedSpotCoordinates.find(
      spot => spot.name === 'Origin',
    );
    if (!originData) {
      return false;
    }

    this.originPoint = namedLatLonCoordinateToOlCoordinate(originData);

    const boundingGeometry = this.createBoundingRectangle();
    this.originFeature = new Feature({
      geometry: boundingGeometry,
    });

    this.featureId = feature.get('id') as string | undefined;
    this.originFeature.setProperties({
      ...this.getDefaultProperties(),
      id: this.featureId,
      labelText: 'Origin',
      isOrigin: true,
      coordinates: [...this.spotCoordinates],
    });
    this.originFeature.setStyle(spotStyleFunction);
    this.editingVectorSource.addFeature(this.originFeature);

    // Create and add point features for all other coordinates
    const pointData = storedSpotCoordinates.filter(
      spot => spot.name !== 'Origin',
    );
    for (const spot of pointData) {
      const olCoordinate = namedLatLonCoordinateToOlCoordinate(spot);
      const pointFeature = new Feature({
        geometry: new Point(olCoordinate),
      });
      pointFeature.setProperties({
        ...this.getDefaultProperties(),
        labelText: spot.name,
        isOrigin: false,
      });
      pointFeature.setStyle(spotStyleFunction);
      this.spotFeatures.push(pointFeature);
      this.editingVectorSource.addFeature(pointFeature);

      // Update direction counter based on loaded labels
      if (isNonEmptyString(spot.name)) {
        const directionMatch = spot.name.match(/^(SW|SE|NE|NW|[WSEN])(\d+)$/);
        if (directionMatch) {
          const direction = directionMatch[1];
          const countValue = directionMatch[2];
          if (!direction || !countValue) {
            continue;
          }
          const count = Number.parseInt(countValue, 10);
          const directionIndex = directions.indexOf(direction);
          if (directionIndex === -1) {
            continue;
          }
          this.directionCounter.set(
            directionIndex,
            Math.max(this.directionCounter.get(directionIndex) ?? 0, count),
          );
        }
      }
    }

    this.feature = this.originFeature;
    return true;
  }

  private reset(): void {
    this.originPoint = null;
    this.originFeature = null;
    this.spotFeatures = [];
    this.spotCoordinates = [];
    this.directionCounter.clear();
  }

  override showStyles(): boolean {
    return false;
  }

  override onStylingUpdated(): void {
    // Update the origin feature with new styling if it exists
    if (this.originFeature) {
      this.originFeature.setProperties({
        ...this.getDefaultProperties(),
        coordinates: [...this.spotCoordinates],
      });
      this.originFeature.setStyle(spotStyleFunction);
      this.markDirty();
    }

    // Update all point features with new styling
    for (const pointFeature of this.spotFeatures) {
      pointFeature.setProperties({
        ...this.getDefaultProperties(),
        labelText: pointFeature.get('labelText') as string | undefined,
      });
      pointFeature.setStyle(spotStyleFunction);
    }
  }

  override async save(): Promise<void> {
    if (!this.originFeature) {
      console.error('Failed to save spot feature, no origin feature found.');
      return;
    }

    // Ensure the origin feature has the latest coordinates
    this.originFeature.setProperties({
      ...this.getDefaultProperties(),
      coordinates: [...this.spotCoordinates],
    });

    await super.save(this.originFeature);
  }
}
